// apps/share-web/src/player-view/chart-surfaces.ts
//
// Поверхности Chart & Lyrics одной песни: панель (боковая на широком экране,
// встроенная — на телефонном экране песни) и полноэкранный слой. Вынесено из
// mount.ts без смены поведения (часть 1а редизайна): тот же блок нужен
// телефонному экрану (song-screen/phone-screen.ts), а две копии проводки слоя
// разошлись бы первым же изменением. Обоснования — в комментариях, переехавших
// вместе с кодом.
//
// Wiring ONLY. Every decision that can be made without a DOM lives in
// chart-wiring.ts with tests; everything else is inside chart-panel.ts and
// chart-fullscreen.ts. This module just connects them.
import { getState, setState } from '../state.js';
import type { ShareManifest } from '@bandway/share-types';
import type { Player } from '../player.js';
import { mountChartPanel, type ChartPanelHandle, type ChartPanelDeps } from './chart-panel.js';
import { mountChartFullscreen, type ChartFullscreenHandle } from './chart-fullscreen.js';
import { chartTextOf, chartLinksOf, hasChart, chartSectionsFromManifest, songMetaLine } from './chart-wiring.js';
import { createFullscreenSlot } from './fullscreen-slot.js';
import type { PositionSource } from '../chart/chart-follow.js';
import { loadReaderPrefs, saveReaderPrefs } from '../chart/reader-prefs.js';
import { setAppTheme } from '../theme-boot.js';
import type { SetlistNav } from './setlist-nav.js';

/**
 * Тональность и колонки ПЕСНИ. Владелец — mountPlayerView (mount.ts): он живёт ровно
 * один монтаж песни и переживает смену экрана на пороге 768 px, когда широкая
 * раскладка и телефонный экран заменяют друг друга (у каждого — свои поверхности).
 */
export interface ChartShared { shift: number; columns: number }

export interface ChartSurfacesDeps {
  manifest: ShareManifest;
  player: Player;
  nav: SetlistNav;
  /** Без него поверхности держат свои значения — так живёт отдельный экран в тестах. */
  shared?: ChartShared;
  createFollow?: ChartPanelDeps['createFollow'];
  onPlayGesture?: () => void;
  onNavigate?: (shareId: string) => void;
}

export interface ChartSurfaces {
  /** Панель чарта. `phone` решает, каким откроется слой по ⤢ (потолок колонок). */
  mountPanel(host: HTMLElement, o: { phone: boolean; onClose: () => void }): ChartPanelHandle;
  openFullscreen(phone: boolean): void;
  /** Читатель уходил из прошлой песни в чтении — открыть слой снова, синхронно. */
  restore(phone: boolean): void;
  destroy(): void;
}

export function createChartSurfaces(d: ChartSurfacesDeps): ChartSurfaces | null {
  const { manifest, player, nav } = d;
  if (!hasChart(manifest)) {
    // This song carries no words. Reading cannot continue, so the flag comes
    // down here instead of waiting: left raised, it would throw the song AFTER
    // this one into full screen with nobody having asked for it. The reader
    // sitting in the mixer taps to go back to reading — the surface never
    // changes on its own.
    if (getState().chartFullscreenOpen) setState({ chartFullscreenOpen: false });
    return null;
  }
  const chartText = chartTextOf(manifest);
  const chartLinks = chartLinksOf(manifest);
  const chartSections = chartSectionsFromManifest(manifest);
  // The position source is the LOCAL player behind the interface the live
  // client will implement. In wave C this one object changes and nothing else.
  // songIdx is structurally 0: a song cannot change inside one mount — main.ts
  // tears the whole song region down (disposeSong) and mounts it again.
  const chartPosition: PositionSource = {
    now: () => ({ songIdx: 0, posSec: player.getPlayhead(), playing: player.isPlaying() }),
  };

  // The key shift and the column count belong HERE, to the song's chart block,
  // and to nothing else. The panel and the full-screen layer are two windows
  // onto ONE chart, and the layer is built afresh on every open — so a cell
  // inside either surface means the two disagree (panel printing D while the
  // layer prints C) and every ✕ throws the reader's transposition away. On a
  // phone the layer is the ONLY surface, so that ✕ is the whole feature.
  // Not state.ts either: there these would outlive the song switch, and the
  // next song has a key of its own. This mount is re-created per song, so the
  // reset comes for free. Смена экрана на пороге 768 px (mountPlayerView) их тоже
  // НЕ трогает: у каждого экрана свои поверхности, а значения лежат в `shared`
  // самого mountPlayerView — владелец песня, а не поверхность и не экран.
  const shared: ChartShared = d.shared ?? { shift: 0, columns: 1 };
  // Кегль — ЕДИНСТВЕННОЕ из трёх, что переживает песню и перезагрузку: это
  // свойство читателя и его экрана («мне мелко»), а не этой песни, в отличие
  // от тональности и колонок. Поэтому у него есть localStorage, а у соседей
  // нет.
  //
  // ⚠️ try/catch обязателен: в приватном режиме обращение к localStorage
  // БРОСАЕТ, а не возвращает null (тот же приём у bw.lib.* в library-view.ts).
  // Words page v2: настройки читателя — общий модуль reader-prefs (один ключ
  // на страницу слов и плеер; старый `bw.chart.fs` мигрируется там же).
  let prefs = loadReaderPrefs();
  // Владелец стампа темы ОДИН — theme-boot.ts. Свой handle здесь означал бы
  // второго: «Auto», выбранный на старте, оставлял бы живого слушателя
  // системной темы поверх выбора, сделанного на этом маршруте.
  setAppTheme(prefs.theme);
  const remember = (patch: Parameters<typeof saveReaderPrefs>[0]): void => {
    prefs = saveReaderPrefs(patch);
    if (patch.theme) setAppTheme(prefs.theme);
  };
  const rememberFontPct = (n: number): void => { remember({ fontPct: n }); };

  // Хост слоя стоит в `body`, а не в регионе плеера, и это пункт приёмки A1-1:
  // «полный экран должен быть полным экраном». Почему одного z-index не хватало
  // и почему из body вопрос порядка слоёв не возникает — в шапке
  // fullscreen-slot.ts. Он же убирает страницу под слоем из обхода Tab.
  const slot = createFullscreenSlot();

  // Объявлены ВЫШЕ всех стрелок, что их читают (dropLayer, closeFullscreen,
  // openFullscreen, колбэки слоя ниже), намеренно: тело стрелки читает переменную
  // в момент вызова, поэтому обратный порядок работал бы случайно — та же
  // ловушка разобрана в комментарии к `follow` в chart-panel.ts.
  let panel: ChartPanelHandle | null = null;
  let layer: ChartFullscreenHandle | null = null;

  // Играть/пауза для слоя. Тот же жест, что у транспорта плеера
  // (transport.ts): на мобильном старт идёт через onPlayGesture, потому что
  // фоновое <audio> на iOS стартует только ВНУТРИ касания.
  const togglePlayback = (): void => {
    if (player.isPlaying()) { player.pause(); return; }
    if (d.onPlayGesture) d.onPlayGesture();
    else void player.play();
  };

  /** Слой снят, но из чтения читатель НЕ выходил: пересборка поверхности. */
  const dropLayer = (): void => {
    layer?.destroy();
    layer = null;
    slot.uncover();
  };

  /** Выход из чтения. Флаг опускается ЗДЕСЬ — читатель ушёл сам. */
  const closeFullscreen = (): void => {
    dropLayer();
    setState({ chartFullscreenOpen: false });
  };

  // Режим поверхности приходит аргументом: значение живёт у вызывающего, потому
  // что его читают и сборка блока, и открытие слоя (потолок колонок у слоя
  // режим-зависимый).
  const openFullscreen = (phone: boolean): void => {
    if (layer) return;
    // Raise the flag on the way in, drop it on the way out, so "the reader is
    // reading" outlives this mount. Set here rather than in the click handler
    // because the restore path below opens the layer too and the invariant
    // "layer mounted ⟺ flag up" should hold in one place.
    setState({ chartFullscreenOpen: true });
    slot.cover();
    layer = mountChartFullscreen(slot.el, {
      text: chartText,
      links: chartLinks,
      songName: manifest.song.name,
      songKey: manifest.song.key,
      bpm: manifest.song.bpm,
      timeSignature: manifest.song.timeSignature,
      createFollow: d.createFollow,
      songMeta: songMetaLine(manifest.song),
      sections: chartSections,
      // Слой знает только имена и порядок; чем открыть соседа — знание
      // ЭТОГО блока, и оно остаётся здесь (см. onNavigate ниже).
      setlist: nav.items.map((it) => ({ songName: it.songName })),
      // −1 travels through as −1. Collapsing it to 0 left the layer with no
      // way to say "this song is not in that setlist", so it printed "1 / 3"
      // about a song that is not one of the three and offered a Next that
      // skipped straight to the second. The transport's prev/next buttons in
      // mount.ts already gate on this index (through nav.prevShareId /
      // nextShareId) — the layer follows suit.
      currentIndex: nav.curIdx,
      isPhone: phone,
      position: chartPosition,
      initialShift: shared.shift,
      initialColumns: shared.columns,
      initialFontPct: prefs.fontPct,
      onFontPctChange: (n) => { rememberFontPct(n); panel?.setFontPct(n); },
      theme: prefs.theme, fit: prefs.fit, follow: prefs.follow,
      showChords: prefs.showChords, showLyrics: prefs.showLyrics,
      // Ведение по темпу в аудио-плеере не нужно: позицию даёт сам плеер.
      followAvailable: false,
      onThemeChange: (v) => remember({ theme: v }),
      onFitChange: (on) => remember({ fit: on }),
      onFollowChange: (on) => remember({ follow: on }),
      onShowChordsChange: (on) => remember({ showChords: on }),
      onShowLyricsChange: (on) => remember({ showLyrics: on }),
      // Транспорт внизу слоя (A1-5б). Команды плееру идут отсюда: слой умеет
      // только читать позицию, играть — власть владельца плеера.
      transport: {
        durationSec: manifest.duration,
        toggle: togglePlayback,
        seek: (sec) => player.seek(sec),
        isLoading: () => (typeof player.isLoaded === 'function' ? !player.isLoaded() : false),
      },
      // The panel is still mounted behind the layer, so it is told too —
      // otherwise leaving full screen drops the reader back onto the old key.
      onShiftChange: (n) => { shared.shift = n; panel?.setShift(n); },
      onColumnsChange: (n) => { shared.columns = n; },
      // Слой отдаёт МЕСТО соседа, а shareId по этому месту находится здесь:
      // в аудио-шейре у каждой песни сетлиста свой шейр, и это знание
      // владельца списка, а не поверхности для чтения. Индекс приходит из
      // того же `nav.items`, которым список и построен, но проверяется всё
      // равно: наружу уходит команда сменить шейр, и на мусоре она увела бы
      // читателя в «invalid share link».
      onNavigate: (at) => {
        const id = nav.items[at]?.shareId;
        if (id) d.onNavigate?.(id);
      },
      onClose: () => closeFullscreen(),
    });
  };

  return {
    mountPanel(host, o) {
      const mounted = mountChartPanel(host, {
        text: chartText,
        links: chartLinks,
        songKey: manifest.song.key,
        bpm: manifest.song.bpm,
        timeSignature: manifest.song.timeSignature,
        createFollow: d.createFollow,
        sections: chartSections,
        position: chartPosition,
        initialShift: shared.shift,
        initialFontPct: prefs.fontPct,
        onFontPctChange: (n) => { rememberFontPct(n); },
        // Escape принадлежит ВЕРХНЕЙ поверхности. Слой стоит поверх панели, и
        // за слоем панели не видно вовсе — сложить её заодно значит вернуть
        // читателя из чтения к панели, которую он не складывал. Знает об этом
        // только вызывающий: два слушателя сидят на одном document, и порядок
        // регистрации тут ничего не решает (chart-escape.ts).
        canEscape: () => layer == null,
        onShiftChange: (n) => { shared.shift = n; },
        onFullscreen: () => openFullscreen(o.phone),
        onClose: o.onClose,
      });
      panel = mounted;
      // Панель, снятую владельцем при пересборке, слой больше не трогает: раньше
      // `chartPanel = null` стоял сразу за destroy(), теперь владелец ссылки —
      // этот модуль, и обнулять её обязан он.
      return {
        ...mounted,
        destroy() {
          mounted.destroy();
          if (panel === mounted) panel = null;
        },
      };
    },
    openFullscreen,
    // Restore reading. The reader moved between songs from inside the layer, and
    // that navigation tore this whole region down — so the layer is re-opened
    // here, SYNCHRONOUSLY, in the same task that built the mixer. Deferring it
    // to a frame or a microtask would let the mixer paint once first, and
    // between two songs on a phone that reads as a flash.
    restore(phone) { if (!layer && getState().chartFullscreenOpen) openFullscreen(phone); },
    destroy() {
      layer?.destroy(); layer = null;
      panel?.destroy(); panel = null;
      // Слот стоит в body, поэтому `host.textContent = ''` у вызывающего до него
      // НЕ достаёт: оставленный, он копился бы по одному на каждую песню сетлиста и
      // держал бы страницу инертной, если монтаж рвался прямо из чтения.
      slot.destroy();
    },
  };
}
